/**
 * A guide's chart (src/content/guides/bodies/<slug>.js `charts`), drawn in the
 * same plain style as the speed benchmark's ranking: white bars on black, the
 * numbers printed beside them. A screen-reader table carries the same values,
 * so the static page and assistive tech get the data too.
 *
 *   { kind: 'bars' | 'columns', title, note?, unit?, decimals?, rows: [{ label, value, display?, highlight? }] }
 *
 * 'bars' ranks rows top to bottom; 'columns' is for an ordered series (days
 * of the week, weeks before departure). `display` overrides the printed value.
 */
import PropTypes from 'prop-types';
import { cn } from '@/lib/utils';

function shown(row, chart) {
  if (row.display) return row.display;
  const n = Number(row.value).toFixed(chart.decimals ?? 0);
  return chart.unit === '$' ? `$${n}` : `${n}${chart.unit || ''}`;
}

/**
 * Vertical bars from a zero line: values above zero rise from it, values
 * below hang under it, so a drop never reads as a rise.
 */
function Columns({ chart }) {
  const up = Math.max(0, ...chart.rows.map((r) => r.value));
  const down = Math.max(0, ...chart.rows.map((r) => -r.value));
  const span = up + down || 1;
  const label = (r) => (
    <span className={cn('shrink-0 text-[11px] sm:text-xs tabular-nums whitespace-nowrap', r.highlight ? 'text-white' : 'text-white/60')}>{shown(r, chart)}</span>
  );
  const bar = (r, part) => (
    <span className={cn('block w-full shrink-0', r.highlight ? 'bg-white' : 'bg-white/45')} style={{ height: `${part * 100}%` }} />
  );
  return (
    <div aria-hidden className="flex gap-1.5 sm:gap-2 h-56 pt-6 pb-6">
      {chart.rows.map((r) => (
        <div key={r.label} className="flex-1 min-w-0 h-full flex flex-col">
          <div className="flex flex-col justify-end items-center border-b border-white/25" style={{ height: `${(up / span) * 100}%` }}>
            {r.value >= 0 && <>{label(r)}<span className="h-1 shrink-0" />{bar(r, up ? r.value / up : 0)}</>}
          </div>
          {down > 0 && (
            <div className="flex flex-col items-center" style={{ height: `${(down / span) * 100}%` }}>
              {r.value < 0 && <>{bar(r, -r.value / down)}<span className="h-1 shrink-0" />{label(r)}</>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
Columns.propTypes = { chart: PropTypes.object.isRequired };

export default function GuideChart({ chart }) {
  const max = Math.max(...chart.rows.map((r) => Math.abs(r.value)), 0) || 1;
  return (
    <figure className="my-10">
      <figcaption className="mb-1 text-[17px] font-medium text-white">{chart.title}</figcaption>
      {chart.note && <p className="mb-5 text-sm text-white/55">{chart.note}</p>}

      {chart.kind === 'columns' ? (
        <Columns chart={chart} />
      ) : (
        <ol aria-hidden className="border-t border-white/15">
          {chart.rows.map((r) => (
            <li key={r.label} className="grid grid-cols-[minmax(7rem,11rem)_1fr_4.5rem] sm:grid-cols-[13rem_1fr_5rem] items-center gap-3 py-2.5 border-b border-white/[0.08]">
              <span className={cn('text-[15px] leading-snug', r.highlight ? 'text-white font-medium' : 'text-white/85')}>{r.label}</span>
              <span className="h-3 bg-white/[0.06]">
                <span className={cn('block h-full', r.highlight ? 'bg-white' : 'bg-white/60')} style={{ width: `${(Math.abs(r.value) / max) * 100}%` }} />
              </span>
              <span className="text-right text-[15px] font-medium tabular-nums">{shown(r, chart)}</span>
            </li>
          ))}
        </ol>
      )}
      {chart.kind === 'columns' && (
        <div aria-hidden className="flex gap-1.5 sm:gap-2 mt-2">
          {chart.rows.map((r) => <span key={r.label} className="flex-1 min-w-0 text-center text-[11px] sm:text-xs text-white/55 truncate">{r.label}</span>)}
        </div>
      )}

      <table className="sr-only">
        <caption>{chart.title}</caption>
        <tbody>
          {chart.rows.map((r) => <tr key={r.label}><th scope="row">{r.label}</th><td>{shown(r, chart)}</td></tr>)}
        </tbody>
      </table>
    </figure>
  );
}

GuideChart.propTypes = {
  chart: PropTypes.shape({
    kind: PropTypes.oneOf(['bars', 'columns']),
    title: PropTypes.string.isRequired,
    note: PropTypes.string,
    unit: PropTypes.string,
    decimals: PropTypes.number,
    rows: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      display: PropTypes.string,
      highlight: PropTypes.bool,
    })).isRequired,
  }).isRequired,
};
